import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'store';
import { updateRfpElementResponse, updateRfpElementScore } from 'store/rfpResponse';
import { openModal } from 'store/modals';
import useUser from 'hooks/useUser';
import QuestionType from 'types/QuestionType';
import QuestionStatus from 'types/QuestionStatus';

import { QuestionSectionWithResponseAndContext } from './useKliparoElement';
import useInitialValues, { FormValues, QuestionFormValues } from './useInitialValues';
import useSchema from './useSchema';

export type { FormValues } from './useInitialValues';

const getResponse = (form: QuestionFormValues): string => {
  switch (form.type) {
    case QuestionType.TEXT:
    case QuestionType.LONG_TEXT:
    case QuestionType.URL:
      return form.value;
    case QuestionType.CHECKBOX:
      return Object.keys(form.value)
        .filter((key) => form.value[key])
        .join(';');
    case QuestionType.SELECTION:
      return form.value?.value ?? '';
    default:
      // @ts-ignore
      throw new Error(`Invalid question type: ${form.type}`);
  }
};

const getKliparoResponses = (form: FormValues) => {
  return Object.getOwnPropertyNames(form.questions).map((key) => {
    const questionId = key as never as keyof typeof form.questions;

    const question = form.questions[questionId];

    return {
      id: question.id,
      questionId,
      responseText: getResponse(question),
    };
  });
};

const useKliparoElementForm = ({
  traro_element: traroElement,
  rfpElementResponse: traroElementResponse,
}: QuestionSectionWithResponseAndContext) => {
  const rfpId = useSelector((state) => state.rfp.data.id);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isReviewer } = useUser();

  const initialValues = useInitialValues(traroElement, traroElementResponse);
  const schema = useSchema(traroElement);

  const saveForm = useCallback(
    async (values: FormValues) => {
      const payload = {
        elementResponseId: values.id,
        responseText: '-',
        score: values.score,
        elementId: traroElement.id as number,
        rfpId,
        kliparoResponses: getKliparoResponses(values),
        questionStatus: values.questionStatus,
      };

      if (isReviewer) {
        return dispatch(updateRfpElementScore(payload)).unwrap();
      } else {
        return dispatch(updateRfpElementResponse(payload)).unwrap();
      }
    },
    [traroElement, rfpId, dispatch],
  );

  const onAutoSave = useCallback(
    async (values: FormValues) => {
      try {
        const data = await saveForm(values);

        if (
          values.questionStatus !== data.question_status &&
          // ENK-222 Do not show status change on going from begin to in progress
          values.questionStatus !== QuestionStatus.BEGIN &&
          data.question_status !== QuestionStatus.IN_PROGRESS
        ) {
          dispatch(openModal({ modal: 'traroElementSubmitModal', data: undefined }));
        }
      } catch {
        dispatch(
          openModal({
            modal: 'errorModal',
            data: {
              message: t('errors.errorSavingResponse'),
            },
          }),
        );
      }
    },
    [t, saveForm, dispatch],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await saveForm(values);

        dispatch(openModal({ modal: 'traroElementSubmitModal', data: undefined }));
      } catch {
        dispatch(
          openModal({
            modal: 'errorModal',
            data: {
              message: t('errors.errorSavingResponse'),
            },
          }),
        );
      }
    },
    [saveForm, dispatch, t],
  );

  return {
    initialValues,
    schema,
    onSubmit,
    onAutoSave,
  } as const;
};

export default useKliparoElementForm;
